export type PhotoPickModalContext =
	| 'default'
	| 'product'
	| 'pos-materials'
	| 'blocks-configuration';

export function isCatalogPhotoPickContext(context: PhotoPickModalContext): boolean {
	return context === 'product';
}

export function usesChooseImageSourceTitle(
	context: PhotoPickModalContext,
	isMasterPosition?: boolean
): boolean {
	return isCatalogPhotoPickContext(context) && !isMasterPosition;
}

export type PhotoPickModalEditorUiConfig = Readonly<{
	showObjectInfoPanel: boolean;
	showMaskModeSwitch: boolean;
}>;

export type PhotoPickModalEditorUiConfigInput = Partial<PhotoPickModalEditorUiConfig>;

export type PhotoPickModalI18nKeys = Readonly<{
	stepSelect: string;
	stepCrop: string;
	objectInfoTitle: string;
	maskShapeTitle: string;
	maskModeProductLabel: string;
	maskModeImageLabel: string;
	maskModeFreeLabel: string;
}>;

export type PhotoPickModalI18nInput = Partial<PhotoPickModalI18nKeys>;

const DEFAULT_I18N: PhotoPickModalI18nKeys = {
	stepSelect: 'PHOTO_PICK_STEP_SELECT',
	stepCrop: 'PHOTO_PICK_STEP_CROP',
	objectInfoTitle: 'PHOTO_EDITOR.PRODUCT_SIZE',
	maskShapeTitle: 'PHOTO_EDITOR.MASK_SHAPE',
	maskModeProductLabel: 'PHOTO_EDITOR.PRODUCT_SIZE',
	maskModeImageLabel: 'PHOTO_EDITOR.MASK_IMAGE',
	maskModeFreeLabel: 'PHOTO_EDITOR.MASK_FREE'
} as const;

type PhotoPickModalContextConfig = Readonly<{
	ui?: PhotoPickModalEditorUiConfigInput;
	i18n?: PhotoPickModalI18nInput;
}>;

const CONTEXT_CONFIG: Record<PhotoPickModalContext, PhotoPickModalContextConfig> = {
	default: {
		ui: {
			showObjectInfoPanel: false,
			showMaskModeSwitch: false
		}
	},
	product: {
		ui: {
			showObjectInfoPanel: true,
			showMaskModeSwitch: true
		}
	},
	'pos-materials': {
		ui: {
			showObjectInfoPanel: true,
			showMaskModeSwitch: true
		},
		i18n: {
			objectInfoTitle: 'COMMON.PLANOGRAM.POS_MATERIAL_SIZE',
			maskModeProductLabel: 'COMMON.PLANOGRAM.POS_MATERIAL_SIZE'
		}
	},
	'blocks-configuration': {
		ui: {
			showObjectInfoPanel: true,
			showMaskModeSwitch: true
		},
		i18n: {
			objectInfoTitle: 'BLOCK_SIZE',
			maskModeProductLabel: 'BLOCK_SIZE'
		}
	}
} as const;

const DEFAULT_UI: PhotoPickModalEditorUiConfig = {
	showObjectInfoPanel: false,
	showMaskModeSwitch: false
} as const;

export function resolvePhotoPickModalUiConfig(
	context: PhotoPickModalContext,
	override?: PhotoPickModalEditorUiConfigInput
): PhotoPickModalEditorUiConfig {
	const preset: PhotoPickModalEditorUiConfigInput | undefined = CONTEXT_CONFIG[context]?.ui;
	const base: PhotoPickModalEditorUiConfig = {
		...DEFAULT_UI,
		...preset
	};
	if (!override) return base as PhotoPickModalEditorUiConfig;
	return {
		...base,
		...override
	} as PhotoPickModalEditorUiConfig;
}

export function resolvePhotoPickModalI18nKeys(
	context: PhotoPickModalContext,
	override?: PhotoPickModalI18nInput
): PhotoPickModalI18nKeys {
	const preset: PhotoPickModalI18nInput | undefined = CONTEXT_CONFIG[context]?.i18n;
	const base: PhotoPickModalI18nKeys = {
		...DEFAULT_I18N,
		...preset
	};
	if (!override) return base as PhotoPickModalI18nKeys;
	return {
		...base,
		...override
	} as PhotoPickModalI18nKeys;
}